/* eslint-disable camelcase */
/* eslint-disable indent */
import { createApi } from '@reduxjs/toolkit/query/react';
import axios from 'axios';

import {
  MODEL_PLURAL_NAME_TO_SINGULAR,
  MODELS_PLURAL_NAMES,
} from 'constants/models';
import { USER_TYPE_ALL } from 'modules/User/contants/types';
import EventService from 'Services/EventService';
import UserService from 'Services/UserService';
import axiosBaseQuery from 'utils/axios/axiosBaseQuery';
import { getErrorMessage } from 'utils/axios/getErrorMessage';
import dayjs from 'utils/dayjs';
import { RequestValidationFailedError } from 'utils/errors';
import getDefaultTimezone from 'utils/timezone/getDefaultTimezone';

import { transformApiEvent, transformApiUser } from '../utils/transforms';
import { VIEW_DAY, VIEW_WEEK } from '../utils/views';

function isRequestAborted(error, signal) {
  return (
    signal?.aborted ||
    axios.isCancel?.(error) ||
    error?.code === 'ERR_CANCELED' ||
    error?.name === 'CanceledError'
  );
}

export default createApi({
  reducerPath: 'dispatchingApi',
  tagTypes: [
    'DispatchingList',
    'DispatchingGet',
    'DispatchingUnassignedList',
    'DispatchingUsers',
  ],
  baseQuery: axiosBaseQuery(),
  endpoints: (build) => ({
    list: build.query({
      queryFn: async ({ date, view, office_id }, { signal }) => {
        let startDate;
        let endDate;

        if (view === VIEW_WEEK) {
          startDate = dayjs.tz(date, getDefaultTimezone()).startOf('week');
          endDate = dayjs.tz(date, getDefaultTimezone()).endOf('week');
        } else {
          startDate =
            view === VIEW_DAY
              ? dayjs
                  .tz(date, getDefaultTimezone())
                  .startOf('day')
                  .subtract(1, 'day')
              : dayjs.tz(date, getDefaultTimezone()).startOf('month');
          endDate =
            view === VIEW_DAY
              ? dayjs.tz(date, getDefaultTimezone()).endOf('day').add(1, 'day')
              : dayjs.tz(date, getDefaultTimezone()).endOf('month');
        }

        const params = {
          filters: [
            {
              name: 'where_start',
              search_sign: '>=',
              value: startDate.toISOString(),
            },

            {
              name: 'where_start',
              search_sign: '<=',
              value: endDate.toISOString(),
            },
          ],
        };

        if (office_id) {
          params.filters.push({
            name: 'office_id',
            search_sign: '=',
            value: office_id,
          });
        }

        try {
          const data = await EventService.list(params, { signal });

          return {
            data: data.map((event) => transformApiEvent(event)),
          };
        } catch (error) {
          if (isRequestAborted(error, signal)) {
            return { error: { status: 'FETCH_ERROR', error: 'Aborted' } };
          }

          throw error;
        }
      },
      providesTags: ['DispatchingList'],
    }),

    myEvents: build.query({
      queryFn: async (
        { date, view, includeAllUsers = false, includeAllTechnicians = false },
        { signal },
      ) => {
        const tz = getDefaultTimezone();
        // Calendar sync stores the date as `YYYY-MM-DDT00:00:00+00:00` (local
        // midnight mislabeled as UTC). Interpreting the whole string as an
        // instant shifts it a month back for timezones behind UTC, so anchor on
        // the encoded `YYYY-MM-DD` wall date instead.
        const anchor = dayjs.tz(String(date).slice(0, 10), tz);

        let startDate;
        let endDate;

        if (view === VIEW_WEEK) {
          startDate = anchor.startOf('week');
          endDate = anchor.endOf('week');
        } else if (view === VIEW_DAY) {
          startDate = anchor.subtract(1, 'day').startOf('day');
          endDate = anchor.add(1, 'day').endOf('day');
        } else {
          startDate = anchor.startOf('month');
          endDate = anchor.endOf('month');
        }

        const params = {
          filter: {
            start: {
              '>=': startDate.format('YYYY-MM-DD HH:mm:ss'),
              '<=': endDate.format('YYYY-MM-DD HH:mm:ss'),
            },
          },
          include_all_users: includeAllUsers ? 1 : 0,
          include_all_technicians: includeAllTechnicians ? 1 : 0,
        };

        try {
          const data = await EventService.myEvents(params, { signal });

          return {
            data: data.map((event) => transformApiEvent(event)),
          };
        } catch (error) {
          if (isRequestAborted(error, signal)) {
            return { error: { status: 'FETCH_ERROR', error: 'Aborted' } };
          }

          throw error;
        }
      },
      providesTags: ['DispatchingList'],
    }),

    listUnassigned: build.query({
      queryFn: async (params, { signal }) => {
        const data = await EventService.listUnassigned({ signal }, params);

        return {
          data: MODELS_PLURAL_NAMES.reduce(
            (result, modelPlural) => [
              ...result,
              ...data[modelPlural].map(({ model_id: modelId, ..._data }) => ({
                id: modelId,
                model_id: modelId,
                model: MODEL_PLURAL_NAME_TO_SINGULAR[modelPlural],
                ..._data,
              })),
            ],
            [],
          ),
        };
      },
      providesTags: ['DispatchingUnassignedList'],
    }),

    get: build.query({
      queryFn: async (id, { signal }) => {
        return {
          data: transformApiEvent(await EventService.get(id, { signal })),
        };
      },
      providesTags: ['DispatchingGet'],
    }),

    users: build.query({
      queryFn: async (
        {
          type = USER_TYPE_ALL,
          office_id = null,
          withAvailability = false,
          task_event = false,
        } = {},
        { signal, getState },
      ) => {
        try {
          if (type === USER_TYPE_ALL) {
            if (task_event) {
              return {
                data: (await UserService.getClientAllUsers({}, { signal })).map(
                  (user) => transformApiUser(user),
                ),
              };
            }

            const clientId = getState()?.login?.user?.client_id ?? null;
            const filter = {
              status: {
                in: ['Active'],
              },
            };

            if (clientId) {
              filter.client_id = {
                in: [clientId],
              };
            }

            return {
              data: (
                await UserService.getUsers(
                  {
                    filter,
                  },
                  { signal },
                )
              ).map((user) => transformApiUser(user)),
            };
          }

          return {
            data: (
              await UserService.getTechnicians(
                { office_id, withAvailability },
                { signal },
              )
            ).map((user) => transformApiUser(user)),
          };
        } catch (error) {
          if (isRequestAborted(error, signal)) {
            return { error: { status: 'FETCH_ERROR', error: 'Aborted' } };
          }

          throw error;
        }
      },
      providesTags: ['DispatchingUsers'],
    }),

    visibleCalendarUsers: build.query({
      queryFn: async (
        { includeAllUsers = false, includeAllTechnicians = false } = {},
        { signal },
      ) => {
        try {
          const data = await UserService.getCalendarVisibleUsers({
            includeAllUsers,
            includeAllTechnicians,
            signal,
          });

          return {
            data: data.map(({ id, name, image }) => ({ id, name, image })),
          };
        } catch (error) {
          if (isRequestAborted(error, signal)) {
            return { error: { status: 'FETCH_ERROR', error: 'Aborted' } };
          }

          throw error;
        }
      },
      providesTags: ['CalendarVisibleUsers'],
    }),

    create: build.mutation({
      queryFn: async (data, { signal }) => {
        try {
          return { data: await EventService.create(data, { signal }) };
        } catch (error) {
          throw new RequestValidationFailedError(getErrorMessage(error));
        }
      },
      invalidatesTags: ['DispatchingList', 'DispatchingUnassignedList'],
    }),

    update: build.mutation({
      queryFn: async ({ id, ...data }, { signal }) => {
        try {
          return { data: await EventService.update(id, data, { signal }) };
        } catch (error) {
          throw new RequestValidationFailedError(getErrorMessage(error));
        }
      },
      invalidatesTags: ['DispatchingList', 'DispatchingGet'],
    }),
  }),
});
